import React, { useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import Tooltip from './Tooltip';
import { formatCurrency } from '../utils/phevCalculations';
import { calculatePhevBreakdown, getLocalizedVehicleName, VehicleSpec } from '../utils/savingsCalculations';
import { pushDataLayerEvent } from '../utils/analytics';

export interface TooltipTranslation {
  en?: Record<string, string>;
  fr?: Record<string, string>;
}

interface Props {
  weekdayKM: number;
  weekendKM: number;
  vehicle: VehicleSpec;
  translation?: TooltipTranslation;
  phevFuelCost: number;
  electricityCost: number;
  currency: string;
  distanceUnit: string;
  year: string;
  iceYearlyCost: number;
  yearlySaving: number;
}

const fmt = (n: number, decimals = 0, lang: string = 'en') =>
  n.toLocaleString(lang, { minimumFractionDigits: decimals, maximumFractionDigits: decimals });

const SavingsDetails: React.FC<Props> = ({
  weekdayKM,
  weekendKM,
  vehicle,
  translation,
  phevFuelCost,
  electricityCost,
  currency,
  distanceUnit,
  year,
  iceYearlyCost,
  yearlySaving,
}) => {
  const { t, i18n } = useTranslation();

  const [open, setOpen] = useState(false);

  const handleToggle = () => {
    const willOpen = !open;
    setOpen(willOpen);
    if (willOpen) {
      pushDataLayerEvent('calculator_expand_details', {
        vehicle_name: getLocalizedVehicleName(vehicle, i18n.language),
      });
    }
  };

  const tooltipSuffix = (key: string): string => {
    const suffix =
      translation?.[i18n.language as keyof TooltipTranslation]?.[key] ??
      translation?.en?.[key] ??
      '';
    return suffix ? ` ${suffix}` : '';
  };

  const {
    totalAnnualKM,
    yearlyElectricKM,
    electricityYearlyCost,
    fuelYearlyCost,
  } = calculatePhevBreakdown(weekdayKM, weekendKM, vehicle, phevFuelCost, electricityCost);

  return (
    <div className="savings-details">
      <button
        className="savings-details__toggle"
        onClick={handleToggle}
        aria-expanded={open}
        aria-controls="savings-details-content"
      >
        {t('howWeCalculated')}
        <span className="savings-details__caret"> {open ? '▴' : '▾'}</span>
      </button>

      <div
        id="savings-details-content"
        className={`savings-grid ${open ? 'is-open' : 'is-collapsed'}`}
        aria-hidden={!open}
        {...(open
          ? {}
          : ({ inert: '' } as unknown as React.HTMLAttributes<HTMLDivElement>))}
      >
        <div className="savings-grid__content">
          <div className="savings-grid__row">
            <div className="savings-grid__label">{t('totalDrivingDistance')}</div>
            <div className="savings-grid__value">{fmt(totalAnnualKM, 0, i18n.language)} {distanceUnit}</div>
          </div>

          <h6 className="savings-grid__section-title">{t('currentVehicle')}</h6>
          <div className="savings-grid__row">
            <div className="savings-grid__label">{t('fuelCostLabel')}</div>
            <h6 className="savings-grid__value">{formatCurrency(iceYearlyCost, currency, i18n.language)} /{year}</h6>
          </div>

          <h6 className="savings-grid__section-title">{getLocalizedVehicleName(vehicle, i18n.language)}</h6>
          <div className="savings-grid__row">
            <div className="savings-grid__label">
              {t('electricRangeLabel')}
              <Tooltip content={t('tooltip_electric_range') + tooltipSuffix('tooltip_electric_range')} />
            </div>
            <h6 className="savings-grid__value">{fmt(vehicle.phevElectricRange, 0, i18n.language)} {distanceUnit}</h6>
          </div>
          <div className="savings-grid__row">
            <div className="savings-grid__label">
              {t('fuelEconomyCombined')}
              <Tooltip content={<><Trans i18nKey="tooltip_fuel_economy" components={{ a: <a /> }} />{tooltipSuffix('tooltip_fuel_economy')}</>} />
            </div>
            <h6 className="savings-grid__value">{fmt(vehicle.phevHybridEconomy, 1, i18n.language)} L / 100 {distanceUnit}</h6>
          </div>

          <div className="savings-grid__row savings-grid__note">
            <div className="savings-grid__label">
              <Trans
                i18nKey="assumptionElectricUse"
                values={{ vehicleName: getLocalizedVehicleName(vehicle, i18n.language), km: `${fmt(yearlyElectricKM, 0, i18n.language)} ${distanceUnit}` }}
                components={{ strong: <strong /> }}
              />
              <Tooltip content={t('tooltip_note') + tooltipSuffix('tooltip_note')} />
            </div>
            <h6 className="savings-grid__value"></h6>
          </div>

          <div className="savings-grid__row">
            <div className="savings-grid__label">{t('electricityCostLabel')}</div>
            <h6 className="savings-grid__value">{formatCurrency(electricityYearlyCost, currency, i18n.language)} /{year}</h6>
          </div>
          <div className="savings-grid__row">
            <div className="savings-grid__label">{t('fuelCostLabel')}</div>
            <h6 className="savings-grid__value">{formatCurrency(fuelYearlyCost, currency, i18n.language)} /{year}</h6>
          </div>

          <div className="savings-grid__row savings-grid__total">
            <div className="savings-grid__label">{t('totalSavings')}</div>
            <h6 className="savings-grid__value savings-grid__value--green">{formatCurrency(yearlySaving, currency, i18n.language)} /{year}</h6>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SavingsDetails;
